import clone from 'lodash.clonedeep';
import uniqBy from 'lodash.uniqby';
import { v4 as uuid } from 'uuid';

import {
  FolderForTheFieldIcon,
  FolderHardhatIcon,
  FolderProjectFilesIcon,
  FolderIcon,
  PencilIcon,
  TrashCanIcon,
  PlusIcon
} from '@adsk/alloy-react-icon';
import { FolderUtil, SortingUtil, CommonUtil, type VirtualFolderItem } from '@adsk/docs-js-sdk';
import EmptyState from '@adsk/nirvana-empty-state';
import { callOrReturn } from '@adsk/nirvana-helpers';
import VirtualizedTree from '@adsk/nirvana-virtualized-tree';

import { MENU_ACTIONS, NODE_CREATE_KEY, PERMISSION_ACTIONS } from './consts';
import { FolderType, MenuOptionVisibleType } from '../types';

let T: any;

export const setT = (t: any) => {
  T = t;
};

// /////////////////////////////////////////////////////////////////// //
// Folder tree searching
// /////////////////////////////////////////////////////////////////// //
export const getTargetFolder = (nodes: FolderType[] = [], key: string): FolderType | undefined => {
  let target;
  nodes.some((node) => {
    if (node.urn === key) {
      target = node;
      return true;
    }

    const subFolder = getTargetFolder(node.folders, key);
    if (subFolder) {
      target = subFolder;
      return true;
    }
    return false;
  });

  return target;
};

const getParentFolder = (nodes: FolderType[], nodeUrn: string): FolderType | undefined => {
  let target;
  nodes.some((node) => {
    const children = node.folders || [];
    if (children.some((child) => child.urn === nodeUrn)) {
      target = node;
      return true;
    }
    const targetChild = getParentFolder(children, nodeUrn);
    if (targetChild) {
      target = targetChild;
      return true;
    }
    return false;
  });
  return target;
};

const getPathNodes = (folders: FolderType[], urn: string): FolderType[] => {
  const pathNodes: FolderType[] = [];

  const _getPathNodes = (folders: FolderType[] = [], urn: string, pathNodes: FolderType[]) => {
    return folders.some((folder) => {
      if (folder.urn === urn) {
        pathNodes.push(folder);
        return true;
      }
      pathNodes.push(folder);
      const hasPathNode = _getPathNodes(folder.folders, urn, pathNodes);
      if (!hasPathNode) {
        pathNodes.splice(-1);
        return false;
      }
      return true;
    });
  };

  _getPathNodes(folders, urn, pathNodes);

  return pathNodes;
};

// /////////////////////////////////////////////////////////////////// //
// Node validation
// /////////////////////////////////////////////////////////////////// //
const isFolderNameExisting = (nodes: FolderType[], label: string, ignoreUrn?: string) => {
  const title = label.trim().toLowerCase();
  return nodes.some((node) => (node.title || '').trim().toLowerCase() === title && node.urn !== ignoreUrn);
};

export const isFolderNameValid = (
  nodes: FolderType[],
  target: FolderType,
  newLabel?: string,
  creatingNodes: FolderType[] = []
) => {
  const label = (newLabel || '').trim().toLowerCase();

  if (!label || isFolderNameExisting(creatingNodes, label, target.urn)) {
    return false;
  }

  const parentFolder = target.parentNodeKey
    ? getTargetFolder(nodes, target.parentNodeKey)
    : getParentFolder(nodes, target.urn);

  if (!parentFolder || !parentFolder.folders || parentFolder.folders.length <= 0) return true;

  return !parentFolder.folders
    .filter((node) => node.urn !== target.urn)
    .some((node) => node.title.toLowerCase() === label);
};

export const validateFolderName = (
  nodes: FolderType[],
  node: FolderType,
  value?: string,
  creatingNodes?: FolderType[]
) => {
  if (!CommonUtil.trim(value)) return T.translate('error.invalidName');
  if (CommonUtil.isStringTooLong(value, 255)) return T.translate('error.tooLong');
  if (CommonUtil.isReservedName(value)) return T.translate('error.reserveName');
  if (CommonUtil.isEndWithPeriod(value)) return T.translate('error.endPeriod');
  if (CommonUtil.hasSpecialCharacter(value)) return T.translate('error.specialChars');
  if (!isFolderNameValid(nodes, node, value, creatingNodes)) return T.translate('error.duplicated');
  return null;
};

const genProperFolderName = (siblings: FolderType[], node: FolderType, index: number, nameGenerator?: any) => {
  const defaultName = `${node.title}.${index}`;
  const name = nameGenerator ? nameGenerator({ siblings, node, index }) || defaultName : defaultName;
  if (!isFolderNameExisting(siblings, name)) {
    return name;
  }
  return genProperFolderName(siblings, node, index + 1, nameGenerator);
};

export const genNewFolderName = (
  nodes: FolderType[],
  parentUrn: string,
  creatingNodes: FolderType[] = [],
  nameGenerator?: any,
  isParentDefaultFolder?: boolean
) => {
  let target;
  if (isParentDefaultFolder && nameGenerator) {
    return nameGenerator({ siblings: nodes, node: {}, index: nodes.length + 1 });
  }
  nodes.some((node) => {
    if (node.urn === parentUrn) {
      const siblings = (node.folders || []).concat(creatingNodes);
      target = genProperFolderName(siblings, node, siblings.length + 1, nameGenerator);
      return true;
    }

    const targetChild = genNewFolderName(node.folders || [], parentUrn, creatingNodes, nameGenerator);
    if (targetChild) {
      target = targetChild;
      return true;
    }
    return false;
  });
  return target;
};

export const getErrorStateProps = ({
  status,
  isDeepLinkError,
  onRetry,
  onLoadTopFolders
}: {
  status?: number;
  isDeepLinkError?: boolean;
  onRetry?: () => any;
  onLoadTopFolders?: () => any;
}) => {
  // Check if is deep link error
  if (isDeepLinkError) {
    return {
      illustration: EmptyState.Illustrations.BUILDING_CONSTRUCTION,
      title: T.translate('error.viewFolder.title'),
      description: T.translate('error.viewFolder.deepLink'),
      buttonLabel: T.translate('buttons.viewAll'),
      onClick: onLoadTopFolders
    };
  }

  // Check based on status code
  switch (status) {
    case 404:
    case 401:
    case 403:
      return {
        illustration: EmptyState.Illustrations.BUILDING_CONSTRUCTION,
        title: T.translate('error.viewFolder.title'),
        description:
          status === 404 ? T.translate('error.viewFolder.notFound') : T.translate('error.viewFolder.noPermission'),
        buttonLabel: T.translate('buttons.viewAll'),
        onClick: onLoadTopFolders
      };

    default:
      return {
        illustration: EmptyState.Illustrations.FOLDER_EMPTY,
        title: T.translate('error.loadFolders'),
        buttonLabel: T.translate('buttons.tryAgain'),
        onClick: onRetry
      };
  }
};

export const getIsAncestor = (ancestor: FolderType, descendant: FolderType, isStrict: boolean = false) => {
  const isAncestor = ancestor?.path && descendant?.path?.startsWith(ancestor.path);
  const isSameFolder = ancestor && descendant && ancestor.urn === descendant.urn;

  if (isStrict) {
    return isAncestor && !isSameFolder;
  } else {
    return isAncestor;
  }
};

// /////////////////////////////////////////////////////////////////// //
// Reducer helpers start
// /////////////////////////////////////////////////////////////////// //
export const setHiddenRootFolder = (treeState: { [key: string]: any }, hiddenFolder: FolderType) => {
  const newState = { ...treeState };
  newState[hiddenFolder.urn] = { ...hiddenFolder, isHiddenRootFolder: true };
  return newState;
};
export const updateExpandedFolder = (folders: FolderType[], folderUrn: string, children: FolderType[]) => {
  const targets = [...folders];
  const targetNode = getTargetFolder(targets, folderUrn);
  if (targetNode) {
    const curChildren = targetNode.folders || [];

    targetNode.folders = children.map((folder) => {
      const curChild = curChildren.find((child) => folder.urn === child.urn);
      // move all sub node to new child
      if (curChild && (curChild.folders || []).length > 0) {
        folder.folders = curChild.folders;
      }
      return folder;
    });
  }

  return targets;
};
export const updateFolder = (folders: FolderType[], folderUrn: string, folder: FolderType) => {
  const newFolders = clone(folders);
  const targetFolder = getTargetFolder(newFolders, folderUrn);
  if (targetFolder) Object.assign(targetFolder, folder);
  return newFolders;
};

const removeSubFolder = (folders: FolderType[] = [], folderUrn: string, parentFolder: FolderType) => {
  folders.forEach((folder) => {
    if (folder.urn !== folderUrn) {
      if (folder.folders && folder.folders?.length > 0) {
        removeSubFolder(folder.folders, folderUrn, folder);
      }
    } else {
      parentFolder.folders = (parentFolder.folders || []).filter((node) => node.urn !== folderUrn);
      parentFolder.hasSubfolders = parentFolder.folders.length > 0;
    }
  });
};

export const removeFolder = (folders: FolderType[], folderUrn: string) => {
  const newFolders: FolderType[] = [];
  const oldFolders = clone(folders);

  // Setup top folder first
  oldFolders.forEach((folder) => {
    if (folder.urn !== folderUrn) {
      newFolders.push(folder);
      if (folder.folders && folder.folders?.length > 0) {
        removeSubFolder(folder.folders, folderUrn, folder);
      }
    }
  });

  return newFolders;
};

export const addFolder = (
  folders: FolderType[],
  treeState: { [key: string]: any },
  parentUrn: string,
  folder: FolderType
) => {
  const newFolders = clone(folders);
  const targetFolder = getTargetFolder(newFolders, parentUrn);
  if (treeState[parentUrn]?.isHiddenRootFolder) {
    const newFolder = { ...folder, permissionActions: treeState[parentUrn].permissionActions };
    newFolders.push(newFolder);
    return newFolders;
  }
  if (!targetFolder) return folders;
  const newFolder = { ...folder, permissionActions: targetFolder.permissionActions };
  targetFolder.folders = [newFolder, ...(targetFolder.folders || []).filter((folder) => folder.urn !== newFolder.urn)];
  targetFolder.hasSubfolders = true;
  return newFolders;
};

export const addPendingTreeNode = (
  treeState: { [key: string]: any },
  folders: FolderType[],
  parentUrn: string,
  nodeNameGenerator?: any
) => {
  const newState = { ...treeState };
  const { creatingNodes = [], ...rest } = newState[parentUrn] || {};
  const parent = getTargetFolder(folders, parentUrn);

  creatingNodes.unshift({
    urn: uuid(),
    parentNodeKey: parentUrn,
    [VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE]: NODE_CREATE_KEY,
    title: genNewFolderName(
      folders,
      parentUrn,
      creatingNodes,
      nodeNameGenerator,
      treeState[parentUrn]?.isHiddenRootFolder
    ),
    configurations: parent?.configurations,
    folderType: parent?.folderType,
    isEditing: true
  });
  rest.creatingNodes = creatingNodes;
  newState[parentUrn] = rest;

  return newState;
};

export const removePendingTreeNode = (
  treeState: { [key: string]: any },
  parentUrn: string,
  folderUrn: string,
  dropKeys: string[]
) => {
  const newState = { ...treeState };
  const { creatingNodes = [], ...rest } = newState[parentUrn] || {};
  dropKeys.forEach((key) => delete rest[key]);
  newState[parentUrn] = { ...rest, creatingNodes: creatingNodes.filter((node: FolderType) => node.urn !== folderUrn) };
  return newState;
};

export const updatePendingTreeNode = (
  treeState: { [key: string]: any },
  parentUrn: string,
  folderUrn: string,
  folder: object
) => {
  const newState = { ...treeState };

  const { creatingNodes = [], ...rest } = newState[parentUrn] || {};
  if (creatingNodes.length <= 0) return newState;
  rest.creatingNodes = creatingNodes.map((extra: FolderType) => {
    if (extra.urn === folderUrn) {
      return { ...extra, ...folder };
    }
    return extra;
  });
  newState[parentUrn] = rest;
  return newState;
};

export const updateTreeNode = (
  treeState: { [key: string]: any },
  folderUrn: string,
  folder: object,
  dropKeys: string[] = []
) => {
  const newState = { ...treeState };
  const nodeState = { ...newState[folderUrn] };
  dropKeys.forEach((key) => delete nodeState[key]);
  newState[folderUrn] = { ...nodeState, ...folder };
  return newState;
};

// /////////////////////////////////////////////////////////////////// //
// Others
// /////////////////////////////////////////////////////////////////// //
export const getFolderIcon = (folder: FolderType) => {
  if (FolderUtil.isSharedFolder(folder)) return folder.hasNS ? FolderForTheFieldIcon : FolderHardhatIcon;
  return folder.hasNS ? FolderProjectFilesIcon : FolderIcon;
};

export const filterSelectedFolders = (folders: FolderType[], selectedUrns: string[]) => {
  return selectedUrns.map((urn) => getTargetFolder(folders, urn)).filter((node) => !!node);
};

/**
 * Cache all selected folder path so to clean nodes on parent node removed.
 */
export const getSelectedFolderPaths = (
  folders: FolderType[],
  selectedKeys: string[],
  pathInfo: {
    [key: string]: string;
  }
) => {
  return selectedKeys.reduce(
    (pre, key) => {
      if (pathInfo[key]) {
        pre[key] = pathInfo[key];
      } else {
        pre[key] = getTargetFolder(folders, key)?.path;
      }
      return pre;
    },
    {} as Record<string, string | undefined>
  );
};

/**
 * Clear all child nodes once the parent folder is deleted.
 * Only care about the loaded folders.
 **/
export const filterSelectedFolderKeys = (
  folders: FolderType[],
  deletedUrn: string,
  selectedKeys: string[],
  pathInfo: {
    [key: string]: string;
  }
) => {
  return selectedKeys.filter((urn) => {
    const path = pathInfo[urn] || getTargetFolder(folders, urn)?.path;
    if (path) {
      const pathUrns = path.split('/') || [];
      if (pathUrns.some((urn) => urn && deletedUrn.includes(urn))) {
        return false;
      }
      return true;
    } else {
      return true;
    }
  });
};

export const getDefaultExpandedKeys = (
  folders: FolderType[] = [],
  expandKeys: string[] = [],
  deepFolderUrn?: string
) => {
  const pathUrns = deepFolderUrn ? getPathNodes(folders, deepFolderUrn).map(({ urn }) => urn) : [];
  const keys = [...expandKeys, ...pathUrns].filter(Boolean);
  return Array.isArray(keys) && keys.length > 0 ? keys.filter((key) => key !== deepFolderUrn) : undefined;
};

const folderTypeOrder = ({ folderType }: FolderType) => {
  switch (folderType) {
    case 'plan':
      return 1;
    case 'drawing':
      return 2;
    case 'shared':
      return 3;
    default:
      return 4;
  }
};

export const folderSorter = (folderA: FolderType, folderB: FolderType) => {
  // Sort folder type first to ensure Distributed folder is ahead of Project Files folder
  const aOrder = folderTypeOrder(folderA);
  const bOrder = folderTypeOrder(folderB);
  if (aOrder > bOrder) return 1;
  if (aOrder < bOrder) return -1;

  // Natural sort folder title
  const rank = SortingUtil.naturalSort(folderA.title, folderB.title);
  if (rank > 0) return 1;
  if (rank < 0) return -1;
  return 0;
};

const recursiveSort = (folders: FolderType[]) => {
  folders.sort(folderSorter);
  folders.forEach((folder) => {
    if (Array.isArray(folder.folders) && folder.folders.length > 0) {
      recursiveSort(folder.folders);
    }
  });
};

export const sortFolders = (folders: FolderType[]) => {
  const result = [...folders];
  recursiveSort(result);
  return result;
};

export const getMappedFolders = (folders: FolderType[], innerState: { [key: string]: any }) => {
  const keys = Object.keys(innerState);
  if (keys.length <= 0) return folders;

  const updated = clone(folders);
  keys.forEach((urn) => {
    if (innerState[urn].isHiddenRootFolder) {
      const { creatingNodes = [] } = innerState[urn];
      if (creatingNodes.length > 0) {
        updated.push(...creatingNodes);
      }
    } else {
      const folder = getTargetFolder(updated, urn);
      if (folder) {
        const { creatingNodes = [], ...rest } = innerState[urn];

        if (creatingNodes.length > 0) {
          folder.folders = [...creatingNodes, ...(folder.folders || [])];
        }

        Object.assign(folder, rest);
      }
    }
  });
  return updated;
};
const setupChild = (folders: FolderType[], folderMaps: Map<string, FolderType[]>) => {
  folders.forEach((folder) => {
    if (folderMaps.has(folder.urn)) {
      const children = folderMaps.get(folder.urn) || [];
      folder.folders = children;
      folder.hasSubfolders = folder.hasSubfolders || children.length > 0;
      children.length > 0 && setupChild(children, folderMaps);
    }
  });
};

/**
 * Rebuild the folders tree with flat folders
 */
const restructureFolders = (folders: FolderType[], buildFromRootFolders: boolean = false) => {
  const roots: FolderType[] = [];
  const topFolders: FolderType[] = [];
  const folderMap = new Map<string, FolderType[]>();

  folders.forEach((folder) => {
    // If current folder has parents or is root, it's a top folder
    const parents = folder.parents || [];
    if (parents.length > 0 || folder.isRoot) {
      topFolders.push(folder);
    }

    const nodes = buildFromRootFolders ? [...parents, folder] : [folder];

    nodes.forEach((current) => {
      const { parentUrn } = current;

      // Clear parents data from folder to avoid `Maximum call stack size exceeded` error when saving to store
      if (current.parents) delete current.parents;

      if (!folderMap.has(parentUrn)) {
        folderMap.set(parentUrn, [current]);
      } else {
        const children = folderMap.get(parentUrn) || [];
        folderMap.set(parentUrn, uniqBy([...children, current], 'urn'));
      }

      if (current.isRoot) roots.push(current);
    });
  });

  const tree = uniqBy(buildFromRootFolders ? roots : topFolders, 'urn');
  setupChild(tree, folderMap);
  return tree;
};

// Calculate and restructure parent folders for top folders
export const setupFoldersWithParents = (fullPath: boolean = false, topFolders: FolderType[] = []) => {
  // Return if
  // - empty top folder
  // - every top folder is root folder
  if (topFolders.length === 0 || !topFolders.some((folder) => !folder.isRoot)) return topFolders;

  // Rebuild folder based on full path flag
  return restructureFolders(topFolders, fullPath);
};

/**
 * Build a nested folder tree from a flat array using parentUrn relationships.
 * Folders whose parentUrn is not found in the array become tree roots.
 */
export const buildFolderTree = (flatFolders: FolderType[]): FolderType[] => {
  if (flatFolders.length === 0) return [];

  const urnSet = new Set(flatFolders.map((f) => f.urn));
  const enriched = new Map(flatFolders.map((f) => [f.urn, { ...f }]));
  const childrenMap = new Map<string, FolderType[]>();

  flatFolders.forEach((folder) => {
    const { parentUrn } = folder;
    if (parentUrn && urnSet.has(parentUrn)) {
      const siblings = childrenMap.get(parentUrn) || [];
      siblings.push(folder);
      childrenMap.set(parentUrn, siblings);
    }
  });

  enriched.forEach((folder) => {
    const children = childrenMap.get(folder.urn);
    if (children) {
      folder.folders = uniqBy(
        children.map((c) => enriched.get(c.urn) as FolderType),
        'urn'
      );
    }
  });

  return flatFolders
    .filter((f) => !f.parentUrn || !urnSet.has(f.parentUrn))
    .map((f) => enriched.get(f.urn) as FolderType);
};

export const getAllTopFolders = async (
  getTopFolders: (offset?: number) => Promise<{ folders: FolderType[]; pagination: { nextUrl?: string } }>
) => {
  let offset = 0;
  const limit = 200;
  const results = await getTopFolders();
  const folders = results.folders || [];
  let nextUrl = results.pagination?.nextUrl;

  while (nextUrl) {
    offset += limit;
    const results = await getTopFolders(offset);
    folders.push(...(results.folders || []));
    nextUrl = results.pagination?.nextUrl;
  }

  return folders;
};

export const getAllVirtualResourceFolders = async (
  getVirtualResourceFolders: (
    offset?: number
  ) => Promise<{ results: VirtualFolderItem[]; pagination: { nextUrl?: string } }>
) => {
  let offset = 0;
  const limit = 50;
  const virtualFolders = [];
  let nextUrl;

  do {
    const { results, pagination } = await getVirtualResourceFolders(offset);
    virtualFolders.push(...(results || []));
    nextUrl = pagination?.nextUrl;
    offset += limit;
  } while (nextUrl);

  return virtualFolders;
};

export const isVirtualRootFolder = (folder: FolderType) => {
  return folder.path === folder.urn && FolderUtil.isVirtualFolder(folder);
};

// /////////////////////////////////////////////////////////////////// //
// Permission validation
// /////////////////////////////////////////////////////////////////// //
export const isPermitEditing = (permissionActions?: string[]) => {
  const actions = (permissionActions || []).filter((action) => !!action).map((action) => action.toUpperCase());
  return actions.includes(PERMISSION_ACTIONS.CONTROL) || actions.includes(PERMISSION_ACTIONS.EDIT);
};

export const isPermissionLoaded = ({ permissionActions }: FolderType) => (permissionActions || []).length > 0;

export const getMenuOptions = (folder: FolderType, menuOptionVisible?: MenuOptionVisibleType) => {
  const options = [];

  const isSharedRoot = FolderUtil.isSharedRootFolder(folder);
  const folderEditable = !folder.isRoot && !isSharedRoot;
  const permitEditing = isPermitEditing(folder.permissionActions);

  const isEnableCreate = callOrReturn(menuOptionVisible, { action: MENU_ACTIONS.CREATE, folder });
  if (isEnableCreate) {
    options.push({
      value: MENU_ACTIONS.CREATE,
      label: T.translate('menu.addSubfolder'),
      disabled: !permitEditing,
      tooltip: !permitEditing ? T.translate('error.noPermission.create') : undefined,
      renderIcon: PlusIcon.renderIcon
    });
  }

  const isEnableRename = folderEditable && callOrReturn(menuOptionVisible, { action: MENU_ACTIONS.RENAME, folder });
  if (isEnableRename) {
    options.push({
      value: MENU_ACTIONS.RENAME,
      label: T.translate('menu.rename'),
      renderIcon: PencilIcon.renderIcon,
      disabled: !permitEditing,
      tooltip: !permitEditing ? T.translate('error.noPermission.rename') : undefined
    });
  }

  const isEnableDelete = folderEditable && callOrReturn(menuOptionVisible, { action: MENU_ACTIONS.DELETE, folder });
  if (isEnableDelete) {
    options.push({
      value: MENU_ACTIONS.DELETE,
      label: T.translate('menu.delete'),
      renderIcon: TrashCanIcon.renderIcon,
      disabled: !permitEditing,
      tooltip: !permitEditing ? T.translate('error.noPermission.delete') : undefined
    });
  }

  return options;
};
